import { defaultConfig, defineConfig } from '@chakra-ui/react'

import { colorPalette } from './colors/colorPalette'
import { buttonRecipe } from './recipes/button'
import { formControlFocusRing, keyboardFocusOutline } from './recipes/focusRing'
import { textStyles } from './textStyles/defaultTextStyles'
import { MangoTheme, ThemeColorMode } from './types'

const chakraSlots = (name: 'nativeSelect' | 'select' | 'combobox') =>
  defaultConfig.theme?.slotRecipes?.[name]?.slots ?? []

// Helper function to convert color palette to v3 token format
export const convertColorPaletteToTokens = (
  palette: Record<string, string>,
) => {
  const tokens: Record<string, { value: string }> = {}
  Object.entries(palette).forEach(([key, value]) => {
    tokens[key] = { value }
  })
  return tokens
}

export const mangoTheme: MangoTheme = {
  breakpoints: {
    base: '0rem',
    xxs: '29rem',
    xs: '47rem',
    sm: '62rem',
    md: '80rem',
    lg: '96rem',
    xl: '119rem',
    '2xl': '500rem',
  },
  textStyles,
  config: {
    initialColorMode: ThemeColorMode.default,
    useSystemColorMode: false,
  },
  colors: { ...colorPalette },
}

const blueColorPaletteGroup = {
  contrast: { value: { base: '{colors.white}', _dark: '{colors.white}' } },
  fg: { value: { base: '{colors.blue.700}', _dark: '{colors.blue.300}' } },
  subtle: { value: { base: '{colors.blue.100}', _dark: '{colors.blue.900}' } },
  muted: { value: { base: '{colors.blue.200}', _dark: '{colors.blue.800}' } },
  emphasized: {
    value: { base: '{colors.blue.300}', _dark: '{colors.blue.700}' },
  },
  solid: { value: { base: '{colors.blue.600}', _dark: '{colors.blue.600}' } },
  focusRing: {
    value: { base: '{colors.blue.400}', _dark: '{colors.blue.400}' },
  },
}

const redColorPaletteGroup = {
  contrast: { value: { base: '{colors.white}', _dark: '{colors.white}' } },
  fg: { value: { base: '{colors.red.700}', _dark: '{colors.red.300}' } },
  subtle: { value: { base: '{colors.red.100}', _dark: '{colors.red.900}' } },
  muted: { value: { base: '{colors.red.200}', _dark: '{colors.red.800}' } },
  emphasized: {
    value: { base: '{colors.red.300}', _dark: '{colors.red.700}' },
  },
  solid: { value: { base: '{colors.red.500}', _dark: '{colors.red.500}' } },
  focusRing: {
    value: { base: '{colors.red.400}', _dark: '{colors.red.400}' },
  },
}

const greenColorPaletteGroup = {
  contrast: { value: { base: '{colors.white}', _dark: '{colors.white}' } },
  fg: { value: { base: '{colors.green.700}', _dark: '{colors.green.300}' } },
  subtle: {
    value: { base: '{colors.green.100}', _dark: '{colors.green.900}' },
  },
  muted: {
    value: { base: '{colors.green.200}', _dark: '{colors.green.800}' },
  },
  emphasized: {
    value: { base: '{colors.green.300}', _dark: '{colors.green.700}' },
  },
  solid: {
    value: { base: '{colors.green.600}', _dark: '{colors.green.600}' },
  },
  focusRing: {
    value: { base: '{colors.green.400}', _dark: '{colors.green.400}' },
  },
}

const orangeColorPaletteGroup = {
  contrast: { value: { base: '{colors.white}', _dark: '{colors.black}' } },
  fg: {
    value: { base: '{colors.orange.700}', _dark: '{colors.orange.300}' },
  },
  subtle: {
    value: { base: '{colors.orange.100}', _dark: '{colors.orange.900}' },
  },
  muted: {
    value: { base: '{colors.orange.200}', _dark: '{colors.orange.800}' },
  },
  emphasized: {
    value: { base: '{colors.orange.300}', _dark: '{colors.orange.700}' },
  },
  solid: {
    value: { base: '{colors.orange.600}', _dark: '{colors.orange.600}' },
  },
  focusRing: {
    value: { base: '{colors.orange.400}', _dark: '{colors.orange.400}' },
  },
}

const grayColorPaletteGroup = {
  contrast: { value: { base: '{colors.white}', _dark: '{colors.black}' } },
  fg: { value: { base: '{colors.gray.800}', _dark: '{colors.gray.200}' } },
  subtle: {
    value: { base: '{colors.gray.100}', _dark: '{colors.gray.900}' },
  },
  muted: {
    value: { base: '{colors.gray.200}', _dark: '{colors.gray.800}' },
  },
  emphasized: {
    value: { base: '{colors.gray.300}', _dark: '{colors.gray.700}' },
  },
  solid: { value: { base: '{colors.gray.900}', _dark: '{colors.white}' } },
  focusRing: {
    value: { base: '{colors.gray.400}', _dark: '{colors.gray.400}' },
  },
}

const purpleColorPaletteGroup = {
  contrast: { value: { base: '{colors.white}', _dark: '{colors.white}' } },
  fg: {
    value: { base: '{colors.purple.700}', _dark: '{colors.purple.300}' },
  },
  subtle: {
    value: { base: '{colors.purple.100}', _dark: '{colors.purple.900}' },
  },
  muted: {
    value: { base: '{colors.purple.200}', _dark: '{colors.purple.800}' },
  },
  emphasized: {
    value: { base: '{colors.purple.300}', _dark: '{colors.purple.700}' },
  },
  solid: {
    value: { base: '{colors.purple.600}', _dark: '{colors.purple.600}' },
  },
  focusRing: {
    value: { base: '{colors.purple.400}', _dark: '{colors.purple.400}' },
  },
}

// Create v3 system configuration
export const config = defineConfig({
  theme: {
    breakpoints: mangoTheme.breakpoints,
    tokens: {
      colors: {
        blue: convertColorPaletteToTokens(colorPalette.blue),
        gray: convertColorPaletteToTokens(colorPalette.gray),
        green: convertColorPaletteToTokens(colorPalette.green),
        red: convertColorPaletteToTokens(colorPalette.red),
        orange: convertColorPaletteToTokens(colorPalette.orange),
        pink: convertColorPaletteToTokens(colorPalette.pink),
        purple: convertColorPaletteToTokens(colorPalette.purple),
        cyan: convertColorPaletteToTokens(colorPalette.cyan),
        teal: convertColorPaletteToTokens(colorPalette.teal),
        yellow: convertColorPaletteToTokens(colorPalette.yellow),
        white: { value: colorPalette.white },
        black: { value: colorPalette.black },
        gradients: {
          mangoBrand: {
            notification_banner: {
              value: colorPalette.gradients.mangoBrand.notification_banner,
            },
            new_action_fab_button: {
              value: colorPalette.gradients.mangoBrand.new_action_fab_button,
            },
            call_sound_indicator: {
              value: colorPalette.gradients.mangoBrand.call_sound_indicator,
            },
            button: { value: colorPalette.gradients.mangoBrand.button },
            popup_background: {
              value: colorPalette.gradients.mangoBrand.popup_background,
            },
          },
        },
        mango: {
          purple: { value: colorPalette.mango.purple },
          aqua: { value: colorPalette.mango.aqua },
          teal: { value: colorPalette.mango.teal },
          blue: { value: colorPalette.mango.blue },
          softBlue: { value: colorPalette.mango.softBlue },
          softPurple: { value: colorPalette.mango.softPurple },
          highlight: { value: colorPalette.mango.highlight },
          orange: { value: colorPalette.mango.orange },
          brand: { value: colorPalette.mango.brand },
        },
      },
    },
    semanticTokens: {
      colors: {
        // Background tokens
        bg: {
          default: {
            value: { base: '{colors.white}', _dark: '{colors.black}' },
          },
          subtle: {
            value: { base: '{colors.gray.50}', _dark: '{colors.gray.950}' },
          },
          muted: {
            value: { base: '{colors.gray.100}', _dark: '{colors.gray.900}' },
          },
          emphasized: {
            value: { base: '{colors.gray.200}', _dark: '{colors.gray.800}' },
          },
          inverted: {
            value: { base: '{colors.black}', _dark: '{colors.white}' },
          },
          panel: {
            value: { base: '{colors.white}', _dark: '{colors.gray.950}' },
          },
          error: {
            value: { base: '{colors.red.50}', _dark: '{colors.red.950}' },
          },
          warning: {
            value: { base: '{colors.orange.50}', _dark: '{colors.orange.950}' },
          },
          success: {
            value: { base: '{colors.green.50}', _dark: '{colors.green.950}' },
          },
          info: {
            value: { base: '{colors.blue.50}', _dark: '{colors.blue.950}' },
          },
          overlay: {
            value: {
              base: 'rgba(0, 0, 0, 0.6)',
              _dark: 'rgba(0, 0, 0, 0.6)',
            },
          },
        },

        // Foreground/text tokens
        fg: {
          default: {
            value: { base: '{colors.black}', _dark: '{colors.gray.50}' },
          },
          subtle: {
            value: { base: '{colors.gray.400}', _dark: '{colors.gray.500}' },
          },
          muted: {
            value: { base: '{colors.gray.600}', _dark: '{colors.gray.400}' },
          },
          inverted: {
            value: { base: '{colors.gray.50}', _dark: '{colors.black}' },
          },
          error: {
            value: { base: '{colors.red.500}', _dark: '{colors.red.400}' },
          },
          warning: {
            value: {
              base: '{colors.orange.600}',
              _dark: '{colors.orange.300}',
            },
          },
          success: {
            value: { base: '{colors.green.600}', _dark: '{colors.green.300}' },
          },
          info: {
            value: { base: '{colors.blue.600}', _dark: '{colors.blue.300}' },
          },
        },

        // Border tokens
        border: {
          default: {
            value: { base: '{colors.gray.200}', _dark: '{colors.gray.800}' },
          },
          subtle: {
            value: { base: '{colors.gray.50}', _dark: '{colors.gray.950}' },
          },
          muted: {
            value: { base: '{colors.gray.100}', _dark: '{colors.gray.900}' },
          },
          emphasized: {
            value: { base: '{colors.gray.300}', _dark: '{colors.gray.700}' },
          },
          inverted: {
            value: { base: '{colors.gray.800}', _dark: '{colors.gray.200}' },
          },
          error: {
            value: { base: '{colors.red.500}', _dark: '{colors.red.400}' },
          },
          warning: {
            value: {
              base: '{colors.orange.500}',
              _dark: '{colors.orange.400}',
            },
          },
          success: {
            value: { base: '{colors.green.500}', _dark: '{colors.green.400}' },
          },
          info: {
            value: { base: '{colors.blue.500}', _dark: '{colors.blue.400}' },
          },
        },

        // Brand tokens (themable)
        brand: {
          contrast: {
            value: { base: '{colors.white}', _dark: '{colors.white}' },
          },
          fg: {
            value: { base: '{colors.blue.700}', _dark: '{colors.blue.300}' },
          },
          subtle: {
            value: { base: '{colors.blue.100}', _dark: '{colors.blue.900}' },
          },
          muted: {
            value: { base: '{colors.blue.200}', _dark: '{colors.blue.800}' },
          },
          emphasized: {
            value: { base: '{colors.blue.300}', _dark: '{colors.blue.700}' },
          },
          solid: {
            value: { base: '{colors.blue.600}', _dark: '{colors.blue.600}' },
          },
          focusRing: {
            value: { base: '{colors.blue.400}', _dark: '{colors.blue.400}' },
          },
        },

        // Blue tokens (non-themable)
        blue: blueColorPaletteGroup,

        // Red tokens (non-themable)
        red: redColorPaletteGroup,

        // Green tokens (non-themable)
        green: greenColorPaletteGroup,

        // Orange tokens (non-themable)
        orange: orangeColorPaletteGroup,

        // Gray tokens (non-themable)
        gray: grayColorPaletteGroup,

        // Pink tokens (non-themable)
        pink: {
          contrast: {
            value: { base: '{colors.white}', _dark: '{colors.white}' },
          },
          fg: {
            value: { base: '{colors.pink.700}', _dark: '{colors.pink.300}' },
          },
          subtle: {
            value: { base: '{colors.pink.100}', _dark: '{colors.pink.900}' },
          },
          muted: {
            value: { base: '{colors.pink.200}', _dark: '{colors.pink.800}' },
          },
          emphasized: {
            value: { base: '{colors.pink.300}', _dark: '{colors.pink.700}' },
          },
          solid: {
            value: { base: '{colors.pink.600}', _dark: '{colors.pink.600}' },
          },
          focusRing: {
            value: { base: '{colors.pink.400}', _dark: '{colors.pink.400}' },
          },
        },

        // Purple tokens (non-themable)
        purple: purpleColorPaletteGroup,

        // Cyan tokens (non-themable)
        cyan: {
          contrast: {
            value: { base: '{colors.white}', _dark: '{colors.white}' },
          },
          fg: {
            value: { base: '{colors.cyan.700}', _dark: '{colors.cyan.300}' },
          },
          subtle: {
            value: { base: '{colors.cyan.100}', _dark: '{colors.cyan.900}' },
          },
          muted: {
            value: { base: '{colors.cyan.200}', _dark: '{colors.cyan.800}' },
          },
          emphasized: {
            value: { base: '{colors.cyan.300}', _dark: '{colors.cyan.700}' },
          },
          solid: {
            value: { base: '{colors.cyan.600}', _dark: '{colors.cyan.600}' },
          },
          focusRing: {
            value: { base: '{colors.cyan.400}', _dark: '{colors.cyan.400}' },
          },
        },

        // Teal tokens (non-themable)
        teal: {
          contrast: {
            value: { base: '{colors.white}', _dark: '{colors.white}' },
          },
          fg: {
            value: { base: '{colors.teal.700}', _dark: '{colors.teal.300}' },
          },
          subtle: {
            value: { base: '{colors.teal.100}', _dark: '{colors.teal.900}' },
          },
          muted: {
            value: { base: '{colors.teal.200}', _dark: '{colors.teal.800}' },
          },
          emphasized: {
            value: { base: '{colors.teal.300}', _dark: '{colors.teal.700}' },
          },
          solid: {
            value: { base: '{colors.teal.600}', _dark: '{colors.teal.600}' },
          },
          focusRing: {
            value: { base: '{colors.teal.400}', _dark: '{colors.teal.400}' },
          },
        },

        // Yellow tokens (non-themable)
        yellow: {
          contrast: {
            value: { base: '{colors.black}', _dark: '{colors.black}' },
          },
          fg: {
            value: {
              base: '{colors.yellow.700}',
              _dark: '{colors.yellow.300}',
            },
          },
          subtle: {
            value: {
              base: '{colors.yellow.100}',
              _dark: '{colors.yellow.900}',
            },
          },
          muted: {
            value: {
              base: '{colors.yellow.200}',
              _dark: '{colors.yellow.800}',
            },
          },
          emphasized: {
            value: {
              base: '{colors.yellow.300}',
              _dark: '{colors.yellow.700}',
            },
          },
          solid: {
            value: {
              base: '{colors.yellow.600}',
              _dark: '{colors.yellow.600}',
            },
          },
          focusRing: {
            value: {
              base: '{colors.yellow.400}',
              _dark: '{colors.yellow.400}',
            },
          },
        },

        // Info tokens (non-themeable, always blue)
        info: blueColorPaletteGroup,

        // Error tokens (non-themeable)
        error: redColorPaletteGroup,

        // Success tokens (non-themeable)
        success: greenColorPaletteGroup,

        // Warning tokens (non-themeable)
        warning: orangeColorPaletteGroup,

        // Neutral tokens (non-themeable)
        neutral: grayColorPaletteGroup,

        // Accent tokens (non-themeable)
        accent: purpleColorPaletteGroup,

        /**
         * The following groups aren't from the design system
         * They are here to help with the transition to the new system
         * We need to work with UX to decide if we should add them to the system or replace theme with other tokens
         */
        // Hover state tokens
        hover: {
          subtle: {
            value: {
              base: 'rgba(82, 82, 91, 0.2)',
              _dark: 'rgba(82, 82, 91, 0.2)',
            },
          },
          default: {
            value: {
              base: 'rgba(82, 82, 91, 0.4)',
              _dark: 'rgba(82, 82, 91, 0.4)',
            },
          },
          emphasized: {
            value: {
              base: 'rgba(63, 63, 70, 0.4)',
              _dark: 'rgba(63, 63, 70, 0.4)',
            },
          },
        },

        // Overlay tokens (modals, backdrops)
        overlay: {
          default: {
            value: {
              base: 'rgba(24, 24, 27, 0.4)',
              _dark: 'rgba(24, 24, 27, 0.4)',
            },
          },
          light: {
            value: {
              base: 'rgba(255, 255, 255, 0.2)',
              _dark: 'rgba(255, 255, 255, 0.2)',
            },
          },
          medium: {
            value: {
              base: 'rgba(255, 255, 255, 0.5)',
              _dark: 'rgba(255, 255, 255, 0.5)',
            },
          },
          dark: {
            value: { base: 'rgba(0, 0, 0, 0.2)', _dark: 'rgba(0, 0, 0, 0.2)' },
          },
        },

        // Skeleton loader tokens
        skeleton: {
          start: {
            value: {
              base: 'rgba(82, 82, 91, 0.2)',
              _dark: 'rgba(82, 82, 91, 0.2)',
            },
          },
          end: {
            value: {
              base: 'rgba(82, 82, 91, 0.5)',
              _dark: 'rgba(82, 82, 91, 0.5)',
            },
          },
        },
      },
      shadows: {
        subtle: {
          value: {
            base: '0px 4px 8px 0px rgba(24, 24, 27, 0.1)',
            _dark: '0px 4px 8px 0px rgba(24, 24, 27, 0.1)',
          },
        },
        default: {
          value: {
            base: '0px 4px 12px 0px rgba(24, 24, 27, 0.2)',
            _dark: '0px 4px 12px 0px rgba(24, 24, 27, 0.2)',
          },
        },
        emphasized: {
          value: {
            base: '0px 4px 8px 0px rgba(24, 24, 27, 0.3)',
            _dark: '0px 4px 8px 0px rgba(24, 24, 27, 0.3)',
          },
        },
        card: {
          value: {
            base: '0 0 1px 0 rgba(24, 24, 27, 0.30), 0 2px 4px 0 rgba(24, 24, 27, 0.10)',
            _dark:
              '0 0 1px 0 rgba(24, 24, 27, 0.30), 0 2px 4px 0 rgba(24, 24, 27, 0.10)',
          },
        },
        focus: {
          value: {
            base: '0px 4px 8px 0px rgba(23, 61, 166, 0.2)',
            _dark: '0px 4px 8px 0px rgba(23, 61, 166, 0.2)',
          },
        },
        focusRing: {
          value: {
            base: '0px 0px 0px 2px rgba(26, 52, 120, 0.5)',
            _dark: '0px 0px 0px 2px rgba(26, 52, 120, 0.5)',
          },
        },
        error: {
          value: {
            base: '0px 4px 8px 0px rgba(220, 38, 38, 0.2)',
            _dark: '0px 4px 8px 0px rgba(220, 38, 38, 0.2)',
          },
        },
        success: {
          value: {
            base: '0px 4px 8px 0px rgba(17, 105, 50, 0.2)',
            _dark: '0px 4px 8px 0px rgba(17, 105, 50, 0.2)',
          },
        },
        tooltip: {
          value: {
            base: '0 2px 8px rgba(0, 0, 0, 0.15)',
            _dark: '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    textStyles: mangoTheme.textStyles as any,
    // Partial recipes merge into Chakra's built-ins; only the focus ring changes.
    recipes: {
      button: buttonRecipe,
      input: { base: formControlFocusRing },
      textarea: { base: formControlFocusRing },
    },
    slotRecipes: {
      nativeSelect: {
        slots: chakraSlots('nativeSelect'),
        base: { field: formControlFocusRing },
      },
      select: {
        slots: chakraSlots('select'),
        base: { trigger: formControlFocusRing },
      },
      combobox: {
        slots: chakraSlots('combobox'),
        base: { input: formControlFocusRing },
      },
    },
  },
  globalCss: {
    // Base layer: recipes, `css` props, and unlayered component styles that
    // draw their own ring replace this one.
    'button:focus-visible': keyboardFocusOutline,
    'input::-webkit-calendar-picker-indicator': {
      backgroundImage: 'url(./svgs/icon-calendar.svg)',
    },
    div: {
      '@supports (-moz-appearance:none)': {
        scrollbarWidth: 'thin',
        scrollbarColor: `${colorPalette.gray[700]} transparent`,
      },
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        width: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        width: '8px',
        borderRadius: '20px',
        border: '1px solid #FFF',
        opacity: 0.7,
        backgroundColor: colorPalette.gray[700],
      },
      '&.scrollbar-hidden::-webkit-scrollbar-thumb': {
        width: '8px',
        borderRadius: '20px',
        border: '1px solid transparent',
        backgroundColor: 'transparent',
      },
      '&.scrollbar-show-on-hover::-webkit-scrollbar-thumb': {
        backgroundColor: 'transparent',
        border: '1px solid transparent',
      },
      '&.scrollbar-show-on-hover:hover::-webkit-scrollbar-thumb': {
        width: '8px',
        borderRadius: '20px',
        border: '1px solid #FFF',
        opacity: 0.7,
        backgroundColor: colorPalette.gray[700],
      },
    },
  },
})
